// Where an instant sits inside one period, counted from that period's own start.
//
// Both figures are 1-based and both restart at the start: the day a period begins is day 1, and its
// first seven days are week 1.
//
// It takes two instants, reduces each to its UTC calendar date, and differences those dates. So a
// result is a whole number of dates rather than of elapsed time, and a part-day between the two
// changes nothing.
//
// UTC is where it reads those dates and the only place it reads them: it consults no ambient
// timezone and never decides for itself which real-world clock an instant belongs to. Deciding that
// is the caller's, which is why a caller hands it instants whose UTC calendar date already IS the
// date it means - read in whichever calendar its own payload is stated on, and said so where it is.
//
// Deliberately NOT the ISO week, which is what the sibling Active groups chart axes label with
// (`activeGroupsWeekAxisLabels`). An ISO week restarts on a Monday having nothing to do with when a
// period began, so it renumbers across a period boundary and can never say "week 3 of this batch".

import { DAY_MS } from './productivityFormatting';

// Whole UTC calendar days from the epoch to `timestampMs`. Differencing two of these is what makes
// the answer a count of dates rather than of elapsed milliseconds.
function utcDayNumber(timestampMs: number): number {
    const instant = new Date(timestampMs);
    return Date.UTC(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate()) / DAY_MS;
}

export function dayInPeriod(periodStartMs: number, timestampMs: number): number {
    return utcDayNumber(timestampMs) - utcDayNumber(periodStartMs) + 1;
}

export function weekInPeriod(periodStartMs: number, timestampMs: number): number {
    return Math.floor((dayInPeriod(periodStartMs, timestampMs) - 1) / 7) + 1;
}

// The tick instants a period-numbered axis draws, one every `stepMs` starting from the period's own
// start rather than from `min`: stepping from `min` lands the first tick on whatever boundary a
// generic interval-from-zero calculation produces, unrelated to what day/week the reader is counting
// from - which is how such an axis loses its own first tick (day/week 1) to a later one instead.
//
// `min` may sit before or after `periodStartMs` (a zoomed-in range, or camera history predating the
// batch): either way this returns the in-range positions on the same start-anchored grid, not a grid
// re-anchored to `min` itself.
export function periodTickPositions(periodStartMs: number, stepMs: number, min: number, max: number): number[] {
    if (!Number.isFinite(periodStartMs) || !Number.isFinite(min) || !Number.isFinite(max) || stepMs <= 0) return [];
    const stepsFromStart = Math.ceil((min - periodStartMs) / stepMs);
    const positions: number[] = [];
    for (let position = periodStartMs + stepsFromStart * stepMs; position <= max; position += stepMs) {
        positions.push(position);
    }
    return positions;
}
